import React, { useCallback, useState } from "react";
import { Icon } from "@iconify/react";
import saveFill from "@iconify/icons-eva/save-outline";
import {
  Dialog,
  TextField,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  IconButton,
  Autocomplete,
} from "@material-ui/core";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
import { LoadingButton } from "@material-ui/lab";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import { useSnackbar } from "notistack";

import { Alert, AlertTitle } from "@material-ui/lab";
import LockIcon from "@material-ui/icons/Lock";

import { clientesApi } from "../../../services/clientesApi";

export default function AgregarClavesPortalesClientes(props) {
  const { open, setOpen, refCliente, setShowAlert } = props;
  const [showPassword, setShowPassword] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // RTK Query mutation
  const [agregarClavePortales] = clientesApi.useAgregarClavePortalesMutation();

  const [tipoClave, setTipoClave] = useState({
    label: "Socio",
    value: "SOCIO",
  });

  /**
   * Se encarga de cerrar el modal
   */
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  /**
   * Validaciones del formulario
   */
  const LoginSchema = Yup.object().shape({
    nombreUsuario: Yup.string().required(
      "Se debe ingresar el nombre de usuario",
    ),
    clave: Yup.string().required("Se debe ingresar una clave"),
    tipoClave: Yup.string().required("Debe seleccionar un tipo de cliente"),
    institucion: Yup.string()
      .required("Se debe ingresar una institución")
      .matches(/^[a-zA-Z0-9\s]+$/, {
        message:
          'La institución no puede contener caracteres especiales como , . " /',
        excludeEmptyString: true,
      }),
  });

  /**
   * Formik setup
   */
  const formik = useFormik({
    initialValues: {
      nombreUsuario: "",
      clave: "",
      tipoClave: "SOCIO",
      institucion: "",
      remember: true,
    },
    validationSchema: LoginSchema,
    onSubmit: () => {
      guardarDatos();
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps, values } =
    formik;

  /**
   * Guardar datos usando RTK Query
   */
  const guardarDatos = useCallback(async () => {
    const clavesCliente = {
      refCliente: refCliente,
      institucion: getFieldProps("institucion").value.toUpperCase(),
      clave: getFieldProps("clave").value,
      tipoClave: getFieldProps("tipoClave").value,
      nombreUsuario: getFieldProps("nombreUsuario").value,
      refSucursal: "CL", // Los clientes usan sucursal "CL"
    };

    try {
      // Usar mutation de RTK Query
      await agregarClavePortales(clavesCliente).unwrap();

      enqueueSnackbar("Clave agregada correctamente", {
        variant: "success",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
      });

      // Mantener compatibilidad con setShowAlert si se usa
      if (setShowAlert) {
        setShowAlert(true);
      }

      handleClose();
    } catch (error) {
      console.error("Error al agregar la clave: ", error);
      enqueueSnackbar(
        error?.data?.message ||
          error.message ||
          "Ocurrió un error al agregar la clave",
        {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        },
      );
    }
  }, [
    agregarClavePortales,
    enqueueSnackbar,
    getFieldProps,
    handleClose,
    refCliente,
    setShowAlert,
  ]);

  const handleShowPassword = () => {
    setShowPassword((showPassword) => !showPassword);
  };

  return (
    <>
      <div>
        <Dialog open={open} aria-labelledby="form-dialog-title">
          <DialogTitle id="form-dialog-title">
            Agregar Clave de Portal
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Ingresa las credenciales de acceso a tus portales institucionales
            </DialogContentText>

            {/* Alert de información sobre encriptación */}
            <Alert
              severity="info"
              icon={<LockIcon />}
              style={{ marginTop: 16, marginBottom: 16 }}
            >
              <AlertTitle>Encriptación Automática</AlertTitle>
              Tu contraseña será encriptada automáticamente con{" "}
              <strong>AES-256-GCM</strong> antes de guardarla. Esto garantiza la
              seguridad de tus credenciales.
            </Alert>

            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <TextField
                  required
                  autoFocus
                  margin="dense"
                  id="nombreUsuario"
                  label="Nombre de Usuario"
                  fullWidth
                  {...getFieldProps("nombreUsuario")}
                  error={Boolean(touched.nombreUsuario && errors.nombreUsuario)}
                  helperText={touched.nombreUsuario && errors.nombreUsuario}
                />

                <TextField
                  required
                  fullWidth
                  autoComplete="new-password"
                  type={showPassword ? "text" : "password"}
                  label="Contraseña"
                  {...getFieldProps("clave")}
                  style={{ marginBottom: 15 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleShowPassword} edge="end">
                          <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={Boolean(touched.clave && errors.clave)}
                  helperText={touched.clave && errors.clave}
                />

                <Autocomplete
                  id="combo-tipo-clave"
                  options={tipoClaveOptions}
                  value={tipoClave}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  getOptionLabel={(option) => option.label}
                  onChange={(event, newValue) => {
                    if (newValue !== null) {
                      values.tipoClave = newValue.value;
                      setTipoClave(newValue);
                    }
                  }}
                  style={{ marginBottom: 15 }}
                  renderInput={(params) => (
                    <TextField
                      required
                      {...params}
                      label="Tipo de Clave"
                      {...getFieldProps("tipoClave")}
                      error={Boolean(touched.tipoClave && errors.tipoClave)}
                      helperText={touched.tipoClave && errors.tipoClave}
                    />
                  )}
                />

                <TextField
                  required
                  margin="dense"
                  id="institucion"
                  label="Institución"
                  placeholder="Ej: SII, PREVIRED, AFP"
                  fullWidth
                  {...getFieldProps("institucion")}
                  error={Boolean(touched.institucion && errors.institucion)}
                  helperText={touched.institucion && errors.institucion}
                />

                <DialogActions>
                  <LoadingButton onClick={handleClose} color="error">
                    Cancelar
                  </LoadingButton>
                  <LoadingButton
                    type="submit"
                    color="primary"
                    startIcon={<Icon icon={saveFill} />}
                    loading={isSubmitting}
                  >
                    Guardar Clave
                  </LoadingButton>
                </DialogActions>
              </Form>
            </FormikProvider>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}

const tipoClaveOptions = [
  { label: "Socio", value: "SOCIO" },
  { label: "Representante Legal", value: "REPRESENTANTE LEGAL" },
  { label: "Empresa", value: "EMPRESA" },
  { label: "Persona natural", value: "PERSONA NATURAL" },
];
